import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, filter, forkJoin, map, mergeMap, Observable, of, tap, throwError } from 'rxjs';
import { SignalRService } from 'src/app/_avmin/services/signalr.service';
import { environment } from 'src/environments/environment';
import { ISaleLineItem } from '../../sales/models/sale-line-item.model';
import { ISalePayment } from '../../sales/models/sale-payment.model';
import { SalesService } from '../../sales/services/sales.service';
import { IGiftCard } from '../../gift-cards/models/gift-card.model';
import { SCOPES } from 'src/app/app.constants';
import { InputModal } from 'src/app/_avmin/components/input-modal/input-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ISalesRegister } from '../../sales-registers/models/sales-register.model';
import { SalesRegisterService } from '../../sales-registers/services/sales-register.service';
import { IPaymentType } from '../../sales/models/payment-type.model';
import { ISale, SaleStatus } from '../../sales/models/sale.model';
import { IBasicUserDetails } from '../../users/models/basic-user-details.model';
import { IUserAccountBalance } from '../../users/models/user-account-balance.model';
import { UserAccountBalanceService } from '../../users/services/user-account-balance.service';
import { IItem } from '../../items/models/item.model';
import { IEvent } from '../../events/models/event.model';
import { IBusiness } from '../../business/models/business.model';
import { BusinessService } from '../../business/services/business.service';
import { AuthService } from '../../auth/services/auth.service';

const API_POS_URL = `${environment.apiUrl}/pos`;
const LS_CURRENT_SALE_ID = 'avmin.sale:current.id';

@Injectable({ providedIn: 'root' })
export class POSService {

  private readonly _authService = inject(AuthService);
  private readonly _businessService = inject(BusinessService);
  private readonly _http = inject(HttpClient);
  private readonly _logger = inject(NGXLogger);
  private readonly _modalService = inject(NgbModal);
  private readonly _registerService = inject(SalesRegisterService);
  private readonly _saleService = inject(SalesService);
  private readonly _signalR = inject(SignalRService);
  private readonly _userAccountBalanceService = inject(UserAccountBalanceService);


  private readonly _business$ = new BehaviorSubject<IBusiness | undefined>(undefined);
  private readonly _businesses$ = new BehaviorSubject<IBusiness[]>([]);
  private readonly _customer$ = new BehaviorSubject<IBasicUserDetails | undefined>(undefined);
  private readonly _customerBalance$ = new BehaviorSubject<IUserAccountBalance | undefined>(undefined);
  private readonly _parkedSales$ = new BehaviorSubject<ISale[]>([]);
  private readonly _sale$ = new BehaviorSubject<ISale | undefined>(undefined);

  public readonly $business$ = this._business$.asObservable();
  public readonly $businesses$ = this._businesses$.asObservable();
  public readonly customer$ = this._customer$.asObservable();
  public readonly customerBalance$ = this._customerBalance$.asObservable();
  public readonly itemCount$ = this._sale$.pipe(map(sale => sale?.lineItems.filter(i => !i.isTracking).length ?? 0));
  public readonly parkedSales$ = this._parkedSales$.asObservable();
  public readonly sale$ = this._sale$.asObservable();

  get customer(): IBasicUserDetails | undefined {
    return this._customer$.getValue();
  }

  get customerBalance(): IUserAccountBalance | undefined {
    return this._customerBalance$.getValue();
  }

  get sale(): ISale | undefined {
    return this._sale$.getValue();
  }

  constructor() {
    this._bindSignalR();
  }

  private _bindSignalR() {
    this._signalR.salesRegister$
      .pipe(
        filter(message => message.id === this._registerService.register?.id)
      )
      .subscribe(message => {
        switch (message.action) {
          case 'saleParked':
          case 'saleRetrieved':
            this._getParkedSales();
            break;
        }
      });
  }

  private _clearSale() {
    localStorage.removeItem(LS_CURRENT_SALE_ID);
    this._business$.next(undefined);
    this._businesses$.next([]);
    this._customer$.next(undefined);
    this._customerBalance$.next(undefined);
    this._sale$.next(undefined);
  }

  private _isSaleTerminal(sale: ISale): boolean {
    if (sale.isClosedOrPaid) { return true; }
    switch (sale.status) {
      case SaleStatus.closed:
      case SaleStatus.paid:
      case SaleStatus.voided:
      case SaleStatus.discarded:
        return true;
    }
    return false;
  }

  private _getAssociatedData(sale: ISale): Observable<ISale> {
    return forkJoin({
      customerBalance: this._getCustomerBalance(sale.customerId, sale.businessId),
      customerBusinesses: this._getCustomerBusinesses(sale.customerId)
    }).pipe(
      map(_ => sale)
    );
  }

  private _getCustomerBusinesses(customerId?: number): Observable<IBusiness[]> {
    if (!customerId) {
      this._businesses$.next([]);
      return of([]);
    }

    const query = this._authService.hasScope(SCOPES.sales.override)
      ? this._businessService.get()
      : this._businessService.forPilot(customerId);

    return query
      .pipe(
        tap(businesses => {
          this._businesses$.next(businesses);
        })
      );
  }

  private _getCurrentOrNewSale(): ISale {
    let sale = this._sale$.getValue();

    if (!sale) {
      this._logger.debug('[POSService]._getCurrentOrNewSale', 'Creating new sale');

      if (!this._registerService.register) {
        throw new Error('No register selected');
      }

      sale = {
        id: 0,
        customerId: this._customer$.getValue()?.id,
        updatedAtUtc: new Date(),
        registerId: this._registerService.register.id,
        amountPaid: 0,
        changeGiven: 0,
        isClosedOrPaid: false,
        lineItems: [],
        payments: [],
        note: '',
        noteInternal: '',
        reference: '',
        register: this._registerService.register,
        remainingToPay: 0,
        status: SaleStatus.unsaved,
        taxes: [],
        totalLoyalty: 0,
        totalPriceInclTax: 0,
        totalTax: 0
      }
    }

    return sale;
  }

  private _getCustomerBalance(customerId?: number, businessId?: number): Observable<IUserAccountBalance | undefined> {
    if (!customerId) {
      this._customerBalance$.next(undefined);
      return of(undefined);
    }

    return this._userAccountBalanceService.get(customerId, businessId)
      .pipe(
        tap(balance => this._customerBalance$.next(balance))
      );
  }

  private _getParkedSales() {
    this._saleService.get({ status: SaleStatus.parked })
      .subscribe(sales => this._parkedSales$.next(sales.items));
  }

  private _updateSale(sale: ISale) {
    localStorage.setItem(LS_CURRENT_SALE_ID, sale.id.toString());
    const business = this._businesses$.getValue().find(b => b.id === sale.businessId);
    this._business$.next(business);
    this._customer$.next(sale.customer)
    this._sale$.next(sale);
  }

  accountPaymentAdd(amount: number): Observable<any> {
    this._logger.debug('[POSService].[accountPaymentAdd]');

    const sale = this._getCurrentOrNewSale();

    if (!sale.customerId) {
      sale.customerId = this._customer$.getValue()?.id;
    }

    return this._http.post<ISale>(`${API_POS_URL}/account_payment`, {
      saleId: sale.id,
      customerId: sale.customerId,
      registerId: sale.registerId,
      amount: +amount
    }).pipe(
      tap(sale => this._updateSale(sale))
    );
  }

  businessSet(business: IBusiness): Observable<any> {
    this._logger.debug('[POSService].[setBusiness]', business);

    const sale = this._getCurrentOrNewSale();
    if (sale.businessId === business.id) {
      this._logger.debug('[POSService].[setBusiness] Business already set');
      return of(undefined);
    }

    this._business$.next(business);
    this._getCustomerBalance(sale.customerId, business.id).subscribe(_ => {});

    if (sale.id === 0) {
      this._logger.debug('[POSService].[setBusiness] Sale not saved yet, setting business on sale object');

      sale.businessId = business.id;
      sale.businessName = business.name;

      this._sale$.next(sale);

      return of(undefined);
    }

    return this._http.post<ISale>(`${API_POS_URL}/business`, {
      businessId: business.id,
      saleId: sale.id
    }).pipe(
      tap(sale => {
        this._logger.debug('[POSService].[setBusiness] Business set on sale', sale);
        this._updateSale(sale);
      })
    );
  }

  closeSale(): Observable<ISale> {
    this._logger.debug('[POSService].[completeSale]');

    const sale = this._sale$.getValue();
    if (!sale) {
      this._logger.debug('[POSService].[completeSale] No sale to complete');
      this._clearSale();
      return throwError(() => 'No sale to complete.');
    }

    if (sale.isClosedOrPaid || sale.status === SaleStatus.voided) {
      this._logger.debug('[POSService].[completeSale] Sale already closed or voided');
      this._clearSale();
      return of(sale);
    }

    if (!this._registerService.register) {
      return throwError(() => 'No register selected');
    }

    return this._http.post<ISale>(`${API_POS_URL}/close`, {
      saleId: sale.id,
      customerId: sale.customerId,
      registerId: this._registerService.register.id
    }).pipe(
      tap(_ => this._clearSale())
    );
  }

  customerSet(customer?: IBasicUserDetails): Observable<any> {
    this._logger.debug('[POSService].[customerSet]', customer);

    const sale = this._getCurrentOrNewSale();
    if (sale.customerId === customer?.id) {
      this._logger.debug('[POSService].[customerSet] Customer already set');
      return of(undefined);
    }

    this._getCustomerBusinesses(customer?.id).subscribe(businesses => {
      this._logger.debug('[POSService].[customerSet] Customer businesses', businesses);

      if (!this._business$.getValue() && businesses.length) {
        let business = businesses.find(b => b.isDefault);
        if (!business) { business = businesses[0]; }
        this._business$.next(business);
      }
    });

    if (sale.id === 0) {
      this._logger.debug('[POSService].[customerSet] Sale not saved yet, setting customer on sale object');

      sale.customer = customer;
      sale.customerId = customer?.id;

      this._getCustomerBalance(customer?.id, sale.businessId).subscribe(balance => {
        this._logger.debug('[POSService].[customerSet] Customer balance', balance);
      });
      this._sale$.next(sale);

      this._customer$.next(customer);

      return of(undefined)
    }

    return this._http.post<ISale>(`${API_POS_URL}/customer`, {
      saleId: sale.id,
      customerId: customer?.id
    }).pipe(
      tap(sale => {
        this._getCustomerBalance(sale.customerId, sale.businessId).subscribe(_ => {});
        this._logger.debug('[POSService].[customerSet] Customer set on sale', sale);
        this._updateSale(sale);
      })
    );
  }

  discard(): Observable<ISale> {
    this._logger.debug('[POSService].[discard]');

    const sale = this._sale$.getValue();
    if (!sale || sale.id < 1) {
      return throwError(() => 'No sale loaded');
    }

    if (!this._registerService.register) {
      return throwError(() => 'No register selected');
    }

    return this._http.post<ISale>(`${API_POS_URL}/discard`, {
      customerId: sale.customerId,
      registerId: this._registerService.register.id,
      saleId: sale.id
    }).pipe(
      tap(_ => this._clearSale())
    );
  }

  init(): Observable<ISalesRegister | undefined> {
    this._logger.debug('[POSService].[init]');

    return forkJoin({
      register: this._registerService.getCurrent()
    }).pipe(
      tap(data => {
        this._logger.debug('[POSService].[init]', data);
        this._getParkedSales();
      }),
      map(data => data.register)
    );
  }

  eventAdd(event: IEvent, item?: IItem): Observable<ISale> {
    this._logger.debug('[POSService].[eventAdd]', event);

    const sale = this._getCurrentOrNewSale();

    if (sale.customerId !== undefined && sale.customerId > 0 && sale.customerId !== event.pilot?.id) {
      return throwError(() => 'Event is for another customer.');
    }

    return this._http.put<ISale>(`${API_POS_URL}/event`, {
      saleId: sale.id,
      customerId: sale.customerId,
      registerId: sale.registerId,
      eventId: event.id,
      itemId: item?.id
    }).pipe(
      mergeMap(sale => this._getAssociatedData(sale)),
      tap(sale => this._updateSale(sale))
    );
  }

  getPayment(paymentId: number): Observable<ISalePayment> {
    return this._http.get<ISalePayment>(`${API_POS_URL}/payment/${paymentId}`);
  }

  giftCardAdd(giftCard: IGiftCard): Observable<ISale> {
    this._logger.debug('[POSService].[giftCardAdd]', giftCard);

    const sale = this._getCurrentOrNewSale();

    return this._http.put<ISale>(`${API_POS_URL}/gift-card`, {
      saleId: sale.id,
      customerId: sale.customerId,
      registerId: sale.registerId,
      giftCardId: giftCard.id
    }).pipe(
      tap(sale => { this._updateSale(sale); })
    );
  }

  itemAdd(item: IItem): Observable<any> {
    this._logger.debug('[POSService].[itemAdd]', item);

    if (item === undefined) { return of(); }

    const sale = this._getCurrentOrNewSale();

    return this._http.put<ISale>(`${API_POS_URL}/item`, {
      saleId: sale.id,
      customerId: sale.customerId,
      registerId: sale.registerId,
      itemId: item.id,
      quantity: 1
    }).pipe(
      tap(sale => { this._updateSale(sale); })
    );
  }

  lineRemove(line: ISaleLineItem): Observable<any> {
    this._logger.debug('[POSService].[lineRemove]', line);

    const sale = this._sale$.getValue();
    if (sale === undefined) { return of(); }

    return this._http.request<ISale>('delete', `${API_POS_URL}/line`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        saleId: sale.id,
        lineItemId: line.id
      })
    }).pipe(
      tap(sale => { this._updateSale(sale); })
    );
  }

  lineUpdate(line: ISaleLineItem, quantity: number, unitPriceInclTax: number, note: string): Observable<any> {
    const sale = this._sale$.getValue();
    if (!sale) { throw new Error('No sale loaded'); }

    return this._http.put<ISale>(`${API_POS_URL}/line`, {
      saleId: sale.id,
      lineItemId: line.id,
      quantity,
      unitPriceInclTax,
      note
    }).pipe(
      tap(sale => this._updateSale(sale))
    );
  }

  loadPendingSale(): Observable<ISale | undefined> {
    const pendingSaleIdValue = localStorage.getItem(LS_CURRENT_SALE_ID);
    if (!pendingSaleIdValue?.length) {
      return of(undefined);
    }

    const pendingSaleId = parseInt(pendingSaleIdValue);
    return this._saleService.find(pendingSaleId)
      .pipe(
        mergeMap<ISale, Observable<ISale | undefined>>(sale => {
          if (this._isSaleTerminal(sale)) {
            this._logger.debug('[POSService].[loadPendingSale] loaded sale is terminal, clearing', sale);
            this._clearSale();
            return of(undefined);
          }
          return this._getAssociatedData(sale).pipe(
            tap(() => this._updateSale(sale)),
            map(() => sale)
          );
        })
      );
  }

  noteSet(note: string): Observable<any> {
    this._logger.debug('[POSService].[noteSet]', note);

    const sale = this._sale$.getValue();
    if (!sale || sale.id < 1) {
      return of(undefined);
    }

    return this._http.post<ISale>(`${API_POS_URL}/note`, {
      saleId: sale.id,
      note
    }).pipe(
      tap(sale => {
        this._updateSale(sale);
      })
    );
  }

  park(): Observable<void> {
    const sale = this._sale$.getValue();
    if (!sale || sale.id < 1) {
      return throwError(() => 'No sale loaded');
    }

    this._logger.debug('[POSService].[park]', sale.id);

    const register = this._registerService.register;

    if (!register) {
      return throwError(() => 'No register selected');
    }

    const modalRef = this._modalService.open(InputModal);

    modalRef.componentInstance.title = 'Add a note to the sale';
    modalRef.componentInstance.label = 'Note';
    modalRef.componentInstance.showLabel = false;
    modalRef.componentInstance.text = 'You are about to park this sale. Add a note so it can be identified by the next person who continues this sale.';
    modalRef.componentInstance.submitButtonText = 'Park sale';
    modalRef.componentInstance.maxLength = 2000;
    modalRef.componentInstance.value = sale.note;

    return modalRef.closed
      .pipe(
        tap((note: string) => this._logger.debug('[POSService].[park]', sale.id, note)),
        mergeMap((note: string) => this._http.post<ISale>(`${API_POS_URL}/park`, {
          customerId: sale.customerId,
          registerId: register.id,
          saleId: sale.id,
          note
        })),
        map(() => this._clearSale())
      );
  }

  parkAllOpenSales(): Observable<void> {
    return this._saleService.parkOpen();
  }

  paymentAdd(paymentType: IPaymentType, amount: number, note: string): Observable<ISale> {
    this._logger.debug('[POSService].[paymentAdd]');

    const sale = this._sale$.getValue();
    if (!sale || sale.id < 1) {
      return throwError(() => 'No sale loaded');
    }

    if (!this._registerService.register) {
      return throwError(() => 'No register selected');
    }

    return this._http.post<ISale>(`${API_POS_URL}/payment`, {
      amount,
      customerId: sale.customerId,
      paymentTypeId: paymentType.id,
      registerId: this._registerService.register.id,
      saleId: sale.id,
      note
    }).pipe(
      mergeMap(sale => this._getCustomerBalance(sale.customerId, sale.businessId).pipe(map(_ => sale))),
      tap(sale => this._updateSale(sale))
    );
  }

  retrieve(sale: ISale): Observable<ISale> {
    this._logger.debug('[POSService].[retrieve]', sale);

    if (!this._registerService.register) {
      return throwError(() => 'No register selected');
    }

    const existingSale = this._sale$.getValue();
    if (!!existingSale && existingSale.id > 0) {
      return throwError(() => 'SALE_IN_PROGRESS');
    }

    return this._http.post<ISale>(`${API_POS_URL}/retrieve`, {
      customerId: sale.customerId,
      registerId: this._registerService.register.id,
      saleId: sale.id
    }).pipe(
      tap(sale => this._updateSale(sale))
    );
  }

}
